/**
 * BrandLogoPanel — Compact Media-Picker für `site_content['brand_logo']`.
 *
 * Bewusst KEIN eigener Admin-Bereich, sondern ein Panel oberhalb der
 * Startseiten-Editor-Tabs. Erlaubt exakt EINE Auswahl: ein Media-Doc aus der
 * Mediathek (Bild ODER Video). Der Renderer im Header (HeaderBrandLogo)
 * entscheidet anhand `media_type` selbst, ob <img> oder <video> ausgeliefert
 * wird — Video-Verhalten (Autoplay, Loop, Muted, Klick-Toggle, Reduced-Motion-
 * Fallback) ist hart in HeaderBrandLogo verdrahtet, kein Toggle im CMS.
 *
 * Save-Flow:
 *   1. PUT /api/admin/site-content/brand_logo  { value: { media_id } }
 *   2. invalidateSiteContentCache() → alle mounted useSiteContent()-Hooks
 *      refetchen live (kein Reload nötig).
 */
import { useCallback, useEffect, useState } from "react";
import { Save, Check, AlertCircle } from "lucide-react";
import { api } from "@/lib/auth";
import { invalidateSiteContentCache } from "@/lib/siteContent";
import type { MediaRef } from "@/lib/media";
import { MediaField } from "@/components/admin/editor/fields";
import type { PickerContext } from "@/components/admin/MediaPicker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL as string;

type BrandLogoValue = {
    media_id?: string | null;
    // Legacy-Felder werden beim Speichern verworfen. Nur `media_id` bleibt.
    [k: string]: unknown;
};

interface Props {
    /** Reicht den globalen MediaPicker-Context aus AdminStartseite durch. */
    setPickerCtx: (ctx: PickerContext) => void;
    /** Callback wenn sich die gespeicherte Media-ID ändert (für ChromaTunePanel). */
    onSavedIdChange?: (id: string | null) => void;
}

export default function BrandLogoPanel({ setPickerCtx, onSavedIdChange }: Props) {
    const [savedId, setSavedId] = useState<string | null>(null);
    const [editId, setEditId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [flash, setFlash] = useState<"ok" | "err" | null>(null);
    const [flashMsg, setFlashMsg] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api<{ items: Record<string, BrandLogoValue> }>(
                "/api/site-content?prefix=brand_logo",
            );
            const val = (res.data?.items?.brand_logo || {}) as BrandLogoValue;
            const id = (val.media_id as string | null) || null;
            setSavedId(id);
            setEditId(id);
            onSavedIdChange?.(id);
        } finally {
            setLoading(false);
        }
    }, [onSavedIdChange]);

    useEffect(() => { void load(); }, [load]);

    const hasChanges = editId !== savedId;

    const doSave = async () => {
        setSaving(true);
        setFlash(null);
        try {
            const res = await api(`/api/admin/site-content/${encodeURIComponent("brand_logo")}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ value: { media_id: editId } }),
            });
            if (!res.ok) throw new Error(res.error || `HTTP ${res.status}`);
            setSavedId(editId);
            onSavedIdChange?.(editId);
            setFlash("ok");
            setFlashMsg("Logo aktualisiert.");
            invalidateSiteContentCache();
            setTimeout(() => setFlash(null), 2200);
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : String(e);
            setFlash("err");
            setFlashMsg(`Fehler beim Speichern: ${msg}`);
        } finally {
            setSaving(false);
        }
    };

    const mediaRef: MediaRef = editId ? { path: "", media_id: editId } : null;

    const openPicker = useCallback((currentType?: string | string[], onPick?: (id: string, path: string) => void) => {
        if (!onPick) return;
        setPickerCtx({ type: currentType, onPick });
    }, [setPickerCtx]);

    return (
        <section
            className="p-5 md:p-6 rounded-md mb-6"
            style={{
                background: "hsl(var(--hf-bg))",
                border: "1px solid hsl(var(--hf-line))",
                boxShadow: "var(--shadow-warm-sm)",
            }}
            data-testid="admin-brand-logo-panel"
        >
            <div className="flex items-baseline justify-between gap-4 mb-4">
                <div>
                    <div className="hf-eyebrow" style={{ fontSize: 10 }}>Branding · Header</div>
                    <h2 className="font-display text-xl md:text-2xl leading-tight mt-0.5">Logo (Bild oder Video)</h2>
                    <p className="text-[13px] mt-1" style={{ color: "hsl(var(--hf-muted))" }}>
                        Wähle ein Bild oder ein Video aus der Mediathek. Der Header erkennt automatisch, wie es dargestellt wird — Videos laufen als stumme Endlos-Loop mit Klick-Pause und Restart bei Seitenwechsel.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={doSave}
                    disabled={!hasChanges || saving || loading}
                    data-testid="admin-brand-logo-save"
                    className="inline-flex items-center gap-2 text-xs md:text-sm px-4 py-2 rounded-sm font-medium disabled:opacity-40 shrink-0"
                    style={{
                        background: hasChanges ? "hsl(var(--hf-gold))" : "hsl(var(--hf-line))",
                        color: "hsl(var(--hf-ink))",
                    }}
                >
                    {flash === "ok" ? <Check size={14} /> : <Save size={14} />}
                    <span>{saving ? "Speichere…" : hasChanges ? "Speichern" : "Gespeichert"}</span>
                </button>
            </div>

            {flash === "err" && (
                <div
                    className="mb-4 p-3 rounded-sm text-sm flex items-start gap-2"
                    style={{ background: "hsl(var(--hf-danger) / 0.12)", color: "hsl(var(--hf-danger))" }}
                    data-testid="admin-brand-logo-error"
                >
                    <AlertCircle size={16} className="mt-0.5" /> {flashMsg}
                </div>
            )}

            {loading ? (
                <div className="text-sm" style={{ color: "hsl(var(--hf-muted))" }}>Lade Logo-Einstellung…</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-start">
                    <MediaField
                        label="Logo-Medium"
                        mediaRef={mediaRef}
                        onChange={(v) => {
                            const nextId = typeof v === "object" && v ? (v.media_id ?? null) : null;
                            setEditId(nextId);
                        }}
                        openPicker={openPicker}
                        mediaType={["photo", "video"]}
                        helpText="PNG mit transparentem Hintergrund oder H.264-MP4 Loop (kurz, ≤ 10 MB)."
                        testId="admin-brand-logo-media"
                    />
                    <div className="w-full md:w-56">
                        <div className="hf-eyebrow mb-1" style={{ fontSize: 10 }}>Live-Vorschau</div>
                        <div
                            className="p-3 rounded-sm flex items-center justify-center min-h-[80px]"
                            style={{ background: "hsl(var(--hf-ink))", border: "1px solid hsl(var(--hf-line))" }}
                            data-testid="admin-brand-logo-preview"
                        >
                            {editId ? (
                                <PreviewMedia mediaId={editId} />
                            ) : (
                                <span className="text-[11px]" style={{ color: "hsl(var(--hf-bg) / 0.55)" }}>Kein Medium gewählt</span>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}

/** Kompakte Vorschau: rendert <video> oder <img> je nach media_type. */
function PreviewMedia({ mediaId }: { mediaId: string }) {
    const [info, setInfo] = useState<{ media_type?: string; poster_url?: string | null } | null>(null);
    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const r = await fetch(`${BACKEND_URL}/api/media/${mediaId}/info`);
                if (r.ok && !cancelled) setInfo(await r.json());
            } catch { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, [mediaId]);
    const isVideo = info?.media_type === "video" || info?.media_type === "video_360";
    if (isVideo) {
        return (
            <video
                key={mediaId}
                src={`${BACKEND_URL}/api/media/${mediaId}`}
                poster={info?.poster_url || `${BACKEND_URL}/api/media/${mediaId}/poster`}
                autoPlay muted loop playsInline
                className="max-h-16 max-w-full w-auto h-auto object-contain"
                data-testid="admin-brand-logo-preview-video"
            />
        );
    }
    return (
        <img
            src={`${BACKEND_URL}/api/media/${mediaId}`}
            alt="Logo-Vorschau"
            className="max-h-16 max-w-full w-auto h-auto object-contain"
            data-testid="admin-brand-logo-preview-image"
        />
    );
}
